import { APIMemberData } from "@companion-core/shared/app/Interfaces/API";
import { getSalutationText } from "@companion-core/shared/app/Utils/componentHelper";
import { getModule } from "@companion-core/shared/app/Utils/config";
import { TFunction } from "i18next";

export const DEFAULT_RECOMMENDED_LIST_RADIUS = [10, 50, 100];

export type RecommendedListProps = {
  from?: string;
  latitude?: string;
  longitude?: string;
  contentType?: string;
  type?: string;
  isLocked?: boolean;
};

export type RecommendedListResolvedParams = {
  from?: string;
  tags?: string[];
  contentType?: string;
  type?: string;
  isLocked?: boolean;
};

export type RecommendedListQueryArgs = {
  from?: string;
  radius?: number;
  tags?: string[];
  contentType?: string;
  type?: string;
  isLocked?: boolean;
  lang?: string;
};

const parseBoolean = (value: string | null) => {
  if (!value) return undefined;
  if (value === "true" || value === "1") return true;
  if (value === "false" || value === "0") return false;
  return undefined;
};

const buildFrom = (searchParams: URLSearchParams) => {
  const from = searchParams.get("from");
  if (from) return from;
  const latitude = searchParams.get("latitude");
  const longitude = searchParams.get("longitude");
  return latitude && longitude ? `${latitude},${longitude}` : undefined;
};

export const getRecommendedListRadiusValues = (): number[] => {
  const radius = getModule("onboarding")?.recommendedList?.radius;
  if (
    Array.isArray(radius) &&
    radius.length > 0 &&
    radius.every((value) => typeof value === "number" && Number.isFinite(value))
  ) {
    return radius;
  }
  return DEFAULT_RECOMMENDED_LIST_RADIUS;
};

export const shouldExpandRecommendedListRadius = ({
  hasFrom,
  radiusIndex,
  radiusCount,
  isError,
  isSuccess,
  resultCount,
}: {
  hasFrom: boolean;
  radiusIndex: number;
  radiusCount: number;
  isError: boolean;
  isSuccess: boolean;
  resultCount: number;
}) => {
  if (!hasFrom || radiusIndex >= radiusCount - 1) return false;
  if (isError) return true;
  return isSuccess && resultCount < 3;
};

// Identifies a search independently of the radius, so a new one restarts the radius ladder.
export const buildRecommendedListSearchKey = (
  queryArgs: Omit<RecommendedListQueryArgs, "radius">
) =>
  [
    queryArgs.from,
    queryArgs.tags?.join(","),
    queryArgs.contentType,
    queryArgs.type,
    queryArgs.isLocked,
    queryArgs.lang,
  ]
    .map((value) => value ?? "")
    .join("|");

export const getRecommendedListLoadingKeys = (isExpanding: boolean) =>
  isExpanding
    ? {
        title: "blocks.recommendedList.loading.expanding.title",
        description: "blocks.recommendedList.loading.expanding.description",
      }
    : {
        title: "blocks.recommendedList.loading.title",
        description: "blocks.recommendedList.loading.description",
      };

export const parseContentFilters = (filters?: string) => {
  if (!filters) {
    return { contentType: undefined, type: undefined, isLocked: undefined };
  }

  const query = filters.startsWith("?") ? filters.slice(1) : filters;
  const params = new URLSearchParams(query);

  return {
    contentType: params.get("content_ct") ?? undefined,
    type: params.get("type") ?? undefined,
    isLocked: parseBoolean(params.get("is_locked")),
  };
};

const resolveFallbackFilters = ({
  searchParams,
  props,
}: {
  searchParams?: URLSearchParams;
  props?: RecommendedListProps;
}) => {
  if (searchParams) {
    return {
      contentType: searchParams.get("content_ct") ?? searchParams.get("contentType") ?? undefined,
      type: searchParams.get("type") ?? undefined,
      isLocked: parseBoolean(searchParams.get("is_locked") ?? searchParams.get("isLocked")),
    };
  }

  return {
    contentType: props?.contentType,
    type: props?.type,
    isLocked: props?.isLocked,
  };
};

export const resolveRecommendedListParams = ({
  searchParams,
  props,
  storeTags,
  storeContentFilters,
}: {
  searchParams?: URLSearchParams;
  props?: RecommendedListProps;
  storeTags?: string[];
  storeContentFilters?: string;
}): RecommendedListResolvedParams => {
  const from = searchParams
    ? buildFrom(searchParams)
    : (props?.from ??
      (props?.latitude && props?.longitude ? `${props.latitude},${props.longitude}` : undefined));
  const storeFilters = parseContentFilters(storeContentFilters);
  const fallbackFilters = resolveFallbackFilters({ searchParams, props });
  const useStoreFilters = Boolean(storeFilters.contentType);

  return {
    from,
    tags: storeTags,
    contentType: useStoreFilters ? storeFilters.contentType : fallbackFilters.contentType,
    type: useStoreFilters ? storeFilters.type : fallbackFilters.type,
    isLocked: useStoreFilters ? storeFilters.isLocked : fallbackFilters.isLocked,
  };
};

export const buildRecommendedListTitle = ({
  member,
  t,
}: {
  member?: APIMemberData;
  t: TFunction;
}) => {
  const salutation = getSalutationText(member?.salutation ?? null, t);
  return t("blocks.recommendedList.results.title", {
    salutation: salutation ?? "",
    firstname: member?.firstname ?? "",
    lastname: member?.lastname ?? "",
  })
    .replace(/\s+/g, " ")
    .trim();
};

const allowedCardVariants = ["vendor", "editorial"] as const;
type RecommendedListCardVariant = (typeof allowedCardVariants)[number];

export const resolveRecommendedListCardVariant = (
  resolvedContentType?: string
): RecommendedListCardVariant => {
  const candidate = resolvedContentType ?? "";
  return allowedCardVariants.includes(candidate as RecommendedListCardVariant)
    ? (candidate as RecommendedListCardVariant)
    : "vendor";
};

export const buildRecommendedListQueryArgs = ({
  resolvedParams,
  lang,
}: {
  resolvedParams: RecommendedListResolvedParams;
  lang?: string;
}): Omit<RecommendedListQueryArgs, "radius"> => {
  return {
    from: resolvedParams.from,
    tags: resolvedParams.tags,
    contentType: resolvedParams.contentType,
    type: resolvedParams.type,
    isLocked: resolvedParams.isLocked,
    lang,
  };
};
